import React, { useEffect, useRef } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import LanguageToggle from './LanguageToggle';
import './AppChrome.css';

/**
 * Global top chrome: Canadian data-residency banner + EN/FR toggle.
 * Sets --app-chrome-height so fixed headers/pages can offset safely.
 */
const AppChrome: React.FC = () => {
  const { t, bannerDismissed, dismissBanner, showBanner } = useLanguage();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const applyHeight = () => {
      const height = Math.ceil(el.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--app-chrome-height', `${height}px`);
    };

    applyHeight();
    const ro = new ResizeObserver(applyHeight);
    ro.observe(el);
    window.addEventListener('resize', applyHeight);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', applyHeight);
      document.documentElement.style.setProperty('--app-chrome-height', '0px');
    };
  }, [bannerDismissed]);

  return (
    <div className="app-chrome" ref={ref}>
      {!bannerDismissed ? (
        <div className="ca-residency-banner" role="region" aria-label={t.residency.title}>
          <div className="ca-residency-banner-text">
            <strong>{t.residency.title}</strong>
            <span>{t.residency.body}</span>
            <em>{t.residency.phase2}</em>
          </div>
          <div className="ca-residency-banner-actions">
            <LanguageToggle compact />
            <button
              type="button"
              className="ca-residency-dismiss"
              onClick={dismissBanner}
              aria-label={t.common.dismiss}
            >
              {t.common.dismiss}
            </button>
          </div>
        </div>
      ) : (
        <div className="app-chrome-slim">
          <button type="button" className="ca-residency-restore" onClick={showBanner}>
            {t.residency.title}
          </button>
          <LanguageToggle compact />
        </div>
      )}
    </div>
  );
};

export default AppChrome;
